import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert,
  Divider, IconButton, Tabs, Tab, TextField, MenuItem, Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Pets as PetsIcon,
  MedicalServices as MedicalIcon, Add as AddIcon,
  CalendarMonth as CalendarIcon, MenuBook as NotebookIcon,
  Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon,
  Vaccines as VaccinesIcon, Warning as WarningIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

// ── Interfaces ──────────────────────────────────────────────────────────────

interface Patient {
  id: number;
  name: string;
  client_id: number;
  owner_name: string;
  species_id: number;
  species: string;
  breed_id: number;
  breed: string;
  date_of_birth: string;
  gender: string;
  weight: number;
  microchip_id: string;
  allergies: string;
  chronic_illnesses: string;
  status: string;
}

interface Species { id: number; name: string; }
interface Breed { id: number; name: string; species_id: number; }

interface FormState {
  name: string;
  species_id: string;
  breed_id: string;
  date_of_birth: string;
  gender: string;
  weight: string;
  microchip_id: string;
  allergies: string;
  chronic_illnesses: string;
  status: string;
}

interface FormErrors {
  name?: string;
  species_id?: string;
  weight?: string;
}

interface MedicalRecord {
  id: number;
  visit_date: string;
  complaint: string;
  diagnosis: string;
  veterinarian_name: string;
  status: string;
}

interface Appointment {
  id: number;
  appointment_date: string;
  appointment_time_start: string;
  status: string;
  reason_for_visit: string;
  veterinarian_name: string;
  service_name: string;
}

interface Prescription {
  id: number;
  prescription_number: string;
  prescription_date: string;
  status: string;
  diagnosis: string;
  veterinarian_name: string;
}

interface Vaccination {
  id: number;
  vaccine_name: string;
  administered_date?: string;
  next_due_date?: string;
  batch_number?: string;
  veterinarian_name?: string;
  notes?: string;
}

interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  total_amount: number;
  payment_status: string;
}

interface NotebookEntry {
  id: number;
  consultation_date: string;
  veterinarian_name: string;
  chief_complaint: string | null;
  diagnosis: string | null;
  treatment_plan: string | null;
  medications_prescribed: string | null;
  follow_up_instructions: string | null;
  patient_weight: number | null;
  symptoms_observed: string | null;
  physical_exam_findings: string | null;
  veterinarian_assessment: string | null;
  lab_results_summary: string | null;
  imaging_results_summary: string | null;
  consultation_duration_minutes: number | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function patientToForm(p: Patient): FormState {
  return {
    name: p.name ?? '',
    species_id: p.species_id ? String(p.species_id) : '',
    breed_id: p.breed_id ? String(p.breed_id) : '',
    date_of_birth: p.date_of_birth ?? '',
    gender: p.gender ?? '',
    weight: p.weight != null ? String(p.weight) : '',
    microchip_id: p.microchip_id ?? '',
    allergies: p.allergies ?? '',
    chronic_illnesses: p.chronic_illnesses ?? '',
    status: p.status ?? 'active',
  };
}

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.name.trim()) errors.name = 'Patient name is required';
  else if (form.name.trim().length < 2) errors.name = 'Name must be at least 2 characters';
  if (!form.species_id) errors.species_id = 'Species is required';
  if (form.weight && (isNaN(Number(form.weight)) || Number(form.weight) <= 0))
    errors.weight = 'Weight must be a positive number';
  return errors;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Completed': case 'Documented': case 'active': case 'Fulfilled': case 'Paid': return 'success';
    case 'Scheduled': return 'info';
    case 'Confirmed': return 'primary';
    case 'Cancelled': return 'error';
    default: return 'default';
  }
};

const getSpeciesColor = (species: string): 'primary' | 'warning' | 'success' | 'secondary' | 'default' => {
  const s = (species || '').toLowerCase();
  if (s.includes('dog') || s.includes('canine')) return 'primary';
  if (s.includes('cat') || s.includes('feline')) return 'warning';
  if (s.includes('bird') || s.includes('avian') || s.includes('parrot')) return 'success';
  return 'secondary';
};

const getSexLabel = (gender?: string): string => {
  if (!gender) return '';
  const g = gender.toLowerCase();
  if (g.includes('neutered') || g.includes('castrated')) return '♂ Neutered';
  if (g.includes('spayed')) return '♀ Spayed';
  if (g === 'male' || (g.startsWith('male') && !g.includes('neutered'))) return '♂ Male';
  if (g === 'female' || (g.startsWith('female') && !g.includes('spayed'))) return '♀ Female';
  if (g === 'unknown') return '';
  return gender;
};

const calculateAge = (dob: string): string => {
  if (!dob) return 'Unknown';
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return 'Unknown';
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  if (now.getDate() < birth.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  if (years < 0) return 'Unknown';
  if (years === 0) return `${months} mo`;
  return months > 0 ? `${years} yr ${months} mo` : `${years} yr`;
};

const fmtDate = (d?: string | null): string => {
  if (!d) return 'Not recorded';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const isDueSoon = (nextDue?: string): boolean => {
  if (!nextDue) return false;
  const due = new Date(nextDue);
  const diffDays = (due.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 30;
};

const isOverdue = (nextDue?: string): boolean => {
  if (!nextDue) return false;
  return new Date(nextDue).getTime() < Date.now();
};

// ── Component ─────────────────────────────────────────────────────────────────

const PatientDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [medicalRecords, setMedicalRecords] = useState<MedicalRecord[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [vaccinations, setVaccinations] = useState<Vaccination[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [notebookEntries, setNotebookEntries] = useState<NotebookEntry[]>([]);
  const [expandedNote, setExpandedNote] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Reference data for dropdowns
  const [speciesList, setSpeciesList] = useState<Species[]>([]);
  const [breedsList, setBreedsList] = useState<Breed[]>([]);
  const [loadingRef, setLoadingRef] = useState(false);

  const fetchPatientData = useCallback(async () => {
    try {
      setLoading(true);
      const [patientRes, recordsRes, apptsRes, rxRes, vaccsRes, invoicesRes, notebookRes] = await Promise.all([
        api.get<Patient>(API_ENDPOINTS.PATIENTS.GET(id!)),
        api.get<{ data: MedicalRecord[] }>(`/medical-records?pet_id=${id}&limit=50`),
        api.get<{ data: Appointment[] }>(`/appointments?pet_id=${id}&limit=50`),
        api.get<{ data: Prescription[] }>(`/prescriptions?pet_id=${id}&limit=50`),
        api.get<{ data: Vaccination[] }>(`/vaccinations?pet_id=${id}&limit=50`),
        api.get<{ data: Invoice[] }>(`/invoicing/invoices?pet_id=${id}`),
        api.get<{ data: NotebookEntry[] }>(`/medical-records/notebook?pet_id=${id}`),
      ]);
      setPatient(patientRes);
      setMedicalRecords(recordsRes.data ?? []);
      setAppointments(apptsRes.data ?? []);
      setPrescriptions(rxRes.data ?? []);
      setVaccinations(vaccsRes.data ?? []);
      setInvoices(invoicesRes.data ?? []);
      setNotebookEntries(notebookRes.data ?? []);
    } catch {
      setError('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (id) fetchPatientData(); }, [id, fetchPatientData]);

  const loadReferenceData = async () => {
    setLoadingRef(true);
    try {
      const [specRes, breedRes] = await Promise.all([
        api.get<{ data: Species[] }>(API_ENDPOINTS.PATIENTS.SPECIES),
        api.get<{ data: Breed[] }>(API_ENDPOINTS.PATIENTS.BREEDS),
      ]);
      setSpeciesList(specRes.data ?? []);
      setBreedsList(breedRes.data ?? []);
    } catch { /* use empty lists */ }
    finally { setLoadingRef(false); }
  };

  const startEdit = async () => {
    if (!patient) return;
    setForm(patientToForm(patient));
    setFormErrors({});
    setSaveError('');
    setSaveSuccess(false);
    setEditMode(true);
    if (speciesList.length === 0) await loadReferenceData();
  };

  const cancelEdit = () => {
    setEditMode(false);
    setForm(null);
    setFormErrors({});
    setSaveError('');
  };

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm(prev => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };
      if (field === 'species_id') next.breed_id = '';
      return next;
    });
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const filteredBreeds = form
    ? breedsList.filter(b => b.species_id === Number(form.species_id))
    : [];

  const handleSave = async () => {
    if (!form) return;
    const errors = validate(form);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        name: form.name.trim(),
        species_id: Number(form.species_id),
        breed_id: form.breed_id ? Number(form.breed_id) : null,
        date_of_birth: form.date_of_birth || null,
        gender: form.gender || null,
        weight: form.weight ? Number(form.weight) : null,
        microchip_id: form.microchip_id || null,
        allergies: form.allergies || null,
        chronic_illnesses: form.chronic_illnesses || null,
        status: form.status,
      };
      const updated = await api.put<Patient>(API_ENDPOINTS.PATIENTS.UPDATE(id!), payload);
      setPatient(updated);
      setEditMode(false);
      setForm(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setSaveError(msg || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Derived stats ─────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const lifetimeSpend = invoices.reduce((s, inv) => s + Number(inv.total_amount || 0), 0);
    const vaccsDueSoon = vaccinations.filter(v => isDueSoon(v.next_due_date)).length;
    const vaccsOverdue = vaccinations.filter(v => isOverdue(v.next_due_date)).length;
    return { lifetimeSpend, vaccsDueSoon, vaccsOverdue };
  }, [invoices, vaccinations]);

  // ── Loading / error states ─────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!patient) {
    return (
      <Box>
        <Alert severity="error">{error || 'Patient not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/patients')} sx={{ mt: 2 }}>
          Back to Patients
        </Button>
      </Box>
    );
  }

  const sexLabel = getSexLabel(patient.gender);
  const ageLabel = patient.date_of_birth ? calculateAge(patient.date_of_birth) : '';

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="flex-start" mb={3} gap={1} flexWrap="wrap">
        <IconButton onClick={() => navigate('/patients')} sx={{ mt: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <PetsIcon sx={{ fontSize: 44, color: 'primary.main', mt: 0.5 }} />
        <Box flex={1} minWidth={200}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h4">{patient.name}</Typography>
            <Chip label={patient.status} size="small" color={getStatusColor(patient.status) as any} />
          </Box>
          {/* Species / breed / sex / age pills */}
          <Stack direction="row" spacing={0.75} flexWrap="wrap" mt={0.75}>
            {patient.species && (
              <Chip label={patient.species} size="small" color={getSpeciesColor(patient.species)} />
            )}
            {patient.breed && (
              <Chip label={patient.breed} size="small" variant="outlined" />
            )}
            {sexLabel && (
              <Chip label={sexLabel} size="small" variant="outlined" />
            )}
            {ageLabel && (
              <Chip label={ageLabel} size="small" variant="outlined" />
            )}
          </Stack>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
          {editMode ? (
            <>
              <Button variant="outlined" size="small" startIcon={<CancelIcon />} onClick={cancelEdit} disabled={saving}>
                Cancel
              </Button>
              <Button variant="contained" size="small" startIcon={saving ? undefined : <SaveIcon />} onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" size="small" startIcon={<MedicalIcon />}
                onClick={() => navigate(`/medical-records?pet_id=${id}`)}>
                Add Record
              </Button>
              <Button variant="outlined" size="small" startIcon={<AddIcon />}
                onClick={() => navigate(`/prescriptions?pet_id=${id}`)}>
                Add Rx
              </Button>
              <Button variant="outlined" size="small" startIcon={<CalendarIcon />}
                onClick={() => navigate('/appointments')}>
                Book Appt
              </Button>
              <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={startEdit}>
                Edit
              </Button>
            </>
          )}
        </Box>
      </Box>

      {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Patient updated successfully.</Alert>}

      <Grid container spacing={3}>
        {/* Left: Patient Info / Edit Form */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            {editMode && form ? (
              <>
                <Typography variant="h6" gutterBottom>Edit Patient</Typography>
                <Divider sx={{ mb: 2 }} />
                {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
                {loadingRef && <CircularProgress size={20} sx={{ mb: 1 }} />}

                <Stack spacing={2}>
                  <TextField
                    label="Patient Name *" fullWidth size="small"
                    value={form.name} onChange={handleChange('name')}
                    error={!!formErrors.name} helperText={formErrors.name}
                  />

                  <TextField
                    select label="Species *" fullWidth size="small"
                    value={form.species_id} onChange={handleChange('species_id')}
                    error={!!formErrors.species_id} helperText={formErrors.species_id}
                  >
                    <MenuItem value="">— Select Species —</MenuItem>
                    {speciesList.map(s => (
                      <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select label="Breed" fullWidth size="small"
                    value={form.breed_id} onChange={handleChange('breed_id')}
                    disabled={!form.species_id || filteredBreeds.length === 0}
                    helperText={form.species_id && filteredBreeds.length === 0 ? 'No breeds for this species' : ''}
                  >
                    <MenuItem value="">— None / Mixed —</MenuItem>
                    {filteredBreeds.map(b => (
                      <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select label="Sex" fullWidth size="small"
                    value={form.gender} onChange={handleChange('gender')}
                  >
                    <MenuItem value="">Unknown</MenuItem>
                    <MenuItem value="Male">Male ♂</MenuItem>
                    <MenuItem value="Female">Female ♀</MenuItem>
                    <MenuItem value="Male (neutered)">Male (neutered) ♂✂</MenuItem>
                    <MenuItem value="Female (spayed)">Female (spayed) ♀✂</MenuItem>
                  </TextField>

                  <TextField
                    label="Date of Birth" type="date" fullWidth size="small"
                    value={form.date_of_birth} onChange={handleChange('date_of_birth')}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: new Date().toISOString().split('T')[0] }}
                  />

                  <TextField
                    label="Weight (kg)" type="number" fullWidth size="small"
                    value={form.weight} onChange={handleChange('weight')}
                    error={!!formErrors.weight} helperText={formErrors.weight}
                    inputProps={{ min: 0, step: 0.1 }}
                  />

                  <TextField
                    label="Microchip ID" fullWidth size="small"
                    value={form.microchip_id} onChange={handleChange('microchip_id')}
                  />

                  <TextField
                    label="Allergies" fullWidth size="small" multiline rows={2}
                    value={form.allergies} onChange={handleChange('allergies')}
                    helperText="List known allergies, separated by commas"
                  />

                  <TextField
                    label="Chronic Conditions" fullWidth size="small" multiline rows={2}
                    value={form.chronic_illnesses} onChange={handleChange('chronic_illnesses')}
                  />

                  <TextField
                    select label="Status" fullWidth size="small"
                    value={form.status} onChange={handleChange('status')}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="deceased">Deceased</MenuItem>
                  </TextField>
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>Patient Information</Typography>
                <Divider sx={{ mb: 2 }} />

                {/* Alerts section */}
                {(patient.allergies || patient.chronic_illnesses) && (
                  <Box
                    sx={{
                      mb: 2, p: 1.5, borderRadius: 1,
                      bgcolor: patient.allergies ? 'error.50' : 'warning.50',
                      border: 1,
                      borderColor: patient.allergies ? 'error.200' : 'warning.200',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <WarningIcon fontSize="small" color={patient.allergies ? 'error' : 'warning'} />
                      <Typography variant="subtitle2" color={patient.allergies ? 'error.main' : 'warning.main'} fontWeight={700}>
                        Alerts
                      </Typography>
                    </Box>
                    {patient.allergies && (
                      <Typography variant="body2" color="error.main">
                        <strong>Allergies:</strong> {patient.allergies}
                      </Typography>
                    )}
                    {patient.chronic_illnesses && (
                      <Typography variant="body2" color="warning.dark" mt={patient.allergies ? 0.5 : 0}>
                        <strong>Chronic conditions:</strong> {patient.chronic_illnesses}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                  <Button size="small" onClick={() => navigate(`/clients/${patient.client_id}`)} sx={{ mt: 0.5, p: 0 }}>
                    {patient.owner_name}
                  </Button>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">Date of Birth / Age</Typography>
                  <Typography variant="body2">
                    {patient.date_of_birth
                      ? `${fmtDate(patient.date_of_birth)} (${calculateAge(patient.date_of_birth)})`
                      : 'Not recorded'}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">Sex</Typography>
                  <Typography variant="body2">{sexLabel || 'Not recorded'}</Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">Weight</Typography>
                  <Typography variant="body2">{patient.weight ? `${patient.weight} kg` : 'Not recorded'}</Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">Microchip ID</Typography>
                  <Typography variant="body2">{patient.microchip_id || 'Not recorded'}</Typography>
                </Box>

                {!patient.allergies && !patient.chronic_illnesses && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">Allergies</Typography>
                    <Typography variant="body2" color="text.secondary">None known</Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* Right: Medical Timeline */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            {/* Quick stats */}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip label={`Visits: ${appointments.length}`} size="small" variant="outlined" />
              <Chip label={`Records: ${medicalRecords.length}`} size="small" variant="outlined" />
              <Chip label={`Rx: ${prescriptions.length}`} size="small" variant="outlined" />
              <Chip
                label={
                  stats.vaccsOverdue > 0
                    ? `${stats.vaccsOverdue} vaccine${stats.vaccsOverdue > 1 ? 's' : ''} overdue`
                    : stats.vaccsDueSoon > 0
                    ? `${stats.vaccsDueSoon} vaccine${stats.vaccsDueSoon > 1 ? 's' : ''} due soon`
                    : `Vaccines: ${vaccinations.length}`
                }
                size="small"
                color={stats.vaccsOverdue > 0 ? 'error' : stats.vaccsDueSoon > 0 ? 'warning' : 'default'}
                variant={stats.vaccsOverdue > 0 || stats.vaccsDueSoon > 0 ? 'filled' : 'outlined'}
                icon={<VaccinesIcon />}
              />
              <Chip
                label={`Lifetime: $${stats.lifetimeSpend.toFixed(2)}`}
                size="small"
                variant="outlined"
              />
            </Stack>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Medical Timeline</Typography>
              <Button variant="outlined" size="small" startIcon={<MedicalIcon />}
                onClick={() => navigate(`/medical-records?pet_id=${id}`)}>
                All Records
              </Button>
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
              <Tab label={`Records (${medicalRecords.length})`} />
              <Tab label={`Appointments (${appointments.length})`} />
              <Tab label={`Prescriptions (${prescriptions.length})`} />
              <Tab icon={<VaccinesIcon />} iconPosition="start" label={`Vaccines (${vaccinations.length})`} />
              <Tab label={`Invoices (${invoices.length})`} />
              <Tab icon={<NotebookIcon />} iconPosition="start" label={`Notes (${notebookEntries.length})`} />
            </Tabs>

            {/* Tab 0: Medical Records */}
            {tab === 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Complaint</TableCell>
                      <TableCell>Diagnosis</TableCell>
                      <TableCell>Veterinarian</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {medicalRecords.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center">No medical records found</TableCell></TableRow>
                    ) : medicalRecords.slice(0, 10).map(record => (
                      <TableRow key={record.id} hover>
                        <TableCell>{fmtDate(record.visit_date)}</TableCell>
                        <TableCell>{record.complaint}</TableCell>
                        <TableCell>{record.diagnosis || '—'}</TableCell>
                        <TableCell>{record.veterinarian_name}</TableCell>
                        <TableCell>
                          <Chip label={record.status} size="small" color={getStatusColor(record.status) as any} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 1: Appointments */}
            {tab === 1 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Vet</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {appointments.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No appointments found</TableCell></TableRow>
                    ) : appointments.slice(0, 10).map(appt => (
                      <TableRow key={appt.id} hover>
                        <TableCell>{fmtDate(appt.appointment_date)}</TableCell>
                        <TableCell>{appt.appointment_time_start}</TableCell>
                        <TableCell>{appt.reason_for_visit}</TableCell>
                        <TableCell>{appt.service_name || '—'}</TableCell>
                        <TableCell>{appt.veterinarian_name}</TableCell>
                        <TableCell>
                          <Chip label={appt.status} size="small" color={getStatusColor(appt.status) as any} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 2: Prescriptions */}
            {tab === 2 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rx #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Diagnosis</TableCell>
                      <TableCell>Prescribed By</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prescriptions.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center">No prescriptions found</TableCell></TableRow>
                    ) : prescriptions.slice(0, 10).map(rx => (
                      <TableRow key={rx.id} hover>
                        <TableCell>{rx.prescription_number}</TableCell>
                        <TableCell>{fmtDate(rx.prescription_date)}</TableCell>
                        <TableCell>{rx.diagnosis || '—'}</TableCell>
                        <TableCell>{rx.veterinarian_name}</TableCell>
                        <TableCell>
                          <Chip label={rx.status} size="small" color={getStatusColor(rx.status) as any} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 3: Vaccinations */}
            {tab === 3 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vaccine</TableCell>
                      <TableCell>Administered</TableCell>
                      <TableCell>Next Due</TableCell>
                      <TableCell>Batch #</TableCell>
                      <TableCell>Vet</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vaccinations.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center">No vaccinations recorded</TableCell></TableRow>
                    ) : vaccinations.map(v => {
                      const overdue = isOverdue(v.next_due_date);
                      const dueSoon = !overdue && isDueSoon(v.next_due_date);
                      return (
                        <TableRow key={v.id} hover>
                          <TableCell><strong>{v.vaccine_name}</strong></TableCell>
                          <TableCell>{fmtDate(v.administered_date)}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" color={overdue ? 'error.main' : dueSoon ? 'warning.main' : 'text.primary'}>
                                {fmtDate(v.next_due_date)}
                              </Typography>
                              {overdue && <Chip label="Overdue" size="small" color="error" />}
                              {dueSoon && <Chip label="Due soon" size="small" color="warning" />}
                            </Box>
                          </TableCell>
                          <TableCell>{v.batch_number || '—'}</TableCell>
                          <TableCell>{v.veterinarian_name || '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 4: Invoices */}
            {tab === 4 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow><TableCell colSpan={4} align="center">No invoices found</TableCell></TableRow>
                    ) : invoices.slice(0, 10).map(inv => (
                      <TableRow key={inv.id} hover>
                        <TableCell>{inv.invoice_number}</TableCell>
                        <TableCell>{fmtDate(inv.invoice_date)}</TableCell>
                        <TableCell align="right">${Number(inv.total_amount).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip label={inv.payment_status} size="small" color={getStatusColor(inv.payment_status) as any} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 5: Consult Notes */}
            {tab === 5 && (
              <Box>
                {notebookEntries.length === 0 ? (
                  <Typography color="text.secondary" align="center" py={3}>No consultation notes recorded yet.</Typography>
                ) : notebookEntries.map(entry => (
                  <Paper key={entry.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }}
                      onClick={() => setExpandedNote(expandedNote === entry.id ? null : entry.id)}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {fmtDate(entry.consultation_date)} — {entry.veterinarian_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {entry.chief_complaint || 'No chief complaint recorded'}
                          {entry.patient_weight ? ` · ${entry.patient_weight} kg` : ''}
                          {entry.consultation_duration_minutes ? ` · ${entry.consultation_duration_minutes} min` : ''}
                        </Typography>
                      </Box>
                      <Chip size="small" label={entry.diagnosis ? 'Diagnosed' : 'In progress'} color={entry.diagnosis ? 'success' : 'default'} />
                    </Box>
                    {expandedNote === entry.id && (
                      <Box sx={{ mt: 1.5 }}>
                        <Divider sx={{ mb: 1.5 }} />
                        {([
                          ['Symptoms Observed', entry.symptoms_observed],
                          ['Physical Exam Findings', entry.physical_exam_findings],
                          ['Assessment', entry.veterinarian_assessment],
                          ['Diagnosis', entry.diagnosis],
                          ['Treatment Plan', entry.treatment_plan],
                          ['Medications Prescribed', entry.medications_prescribed],
                          ['Follow-up Instructions', entry.follow_up_instructions],
                          ['Lab Results Summary', entry.lab_results_summary],
                          ['Imaging Results Summary', entry.imaging_results_summary],
                        ] as [string, string | null][]).filter(([, v]) => v).map(([label, value]) => (
                          <Box key={label} mb={1}>
                            <Typography variant="caption" fontWeight={700} color="primary">{label}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PatientDetailPage;
